import { useCallback, useEffect, useRef, useState } from "react";
import { BlockAccumulator, encodeRadioFrame, resampleLinear } from "../lib/radioFrames";
import { EnergyVad } from "../lib/radioVad";
import {
  RADIO_BLOCK_SAMPLES,
  RADIO_DEFAULT_LABEL,
  RADIO_LABEL_MAX_LENGTH,
  RADIO_LABEL_STORAGE_KEY,
  RADIO_SAMPLE_RATE,
  RADIO_THRESHOLD_DEFAULT_DB,
  RADIO_THRESHOLD_MAX_DB,
  RADIO_THRESHOLD_MIN_DB,
  RADIO_THRESHOLD_STORAGE_KEY,
  parseRadioServerMessage,
  type RadioCaptureState,
  type RadioInputDevice,
  type RadioTranscript,
} from "../types/radio";

const BACKOFF_SCHEDULE_MS = [1000, 2000, 5000, 10000];
const LEVEL_PUBLISH_MS = 100;
const SCRIPT_PROCESSOR_BUFFER = 4096;
const WORKLET_NAME = "vtms-radio-capture";

const WORKLET_SOURCE = `
class RadioCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel) this.port.postMessage(channel.slice(0));
    return true;
  }
}
registerProcessor(${JSON.stringify(WORKLET_NAME)}, RadioCaptureProcessor);
`;

export interface UseRadioCaptureOptions {
  /** Same-origin by default (like apiFetch); set to reach a split dev server. */
  serverUrl: string;
  onTranscript: (transcript: RadioTranscript) => void;
}

export interface RadioCaptureHandle {
  supported: boolean;
  devices: RadioInputDevice[];
  selectedDeviceId: string;
  selectDevice: (deviceId: string) => void;
  refreshDevices: () => Promise<void>;
  label: string;
  setLabel: (label: string) => void;
  thresholdDb: number;
  setThresholdDb: (db: number) => void;
  enabled: boolean;
  start: () => Promise<void>;
  stop: () => void;
  state: RadioCaptureState;
  error: string | null;
  levelDb: number;
  vadOpen: boolean;
  sentFrames: number;
}

export function deriveRadioWsUrl(serverUrl: string): string {
  const base = serverUrl.trim() || (typeof location !== "undefined" ? location.origin : "http://localhost");
  const url = new URL(base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${url.pathname.replace(/\/+$/, "")}/radio-audio`;
  url.search = "";
  url.hash = "";
  return url.toString();
}

export function sanitizeRadioLabel(raw: string): string {
  const trimmed = raw.replace(/\s+/g, " ").trim().slice(0, RADIO_LABEL_MAX_LENGTH);
  return trimmed || RADIO_DEFAULT_LABEL;
}

export function clampThresholdDb(db: number): number {
  if (!Number.isFinite(db)) return RADIO_THRESHOLD_DEFAULT_DB;
  return Math.min(RADIO_THRESHOLD_MAX_DB, Math.max(RADIO_THRESHOLD_MIN_DB, Math.round(db)));
}

function readStorage(key: string): string | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage.getItem(key);
  } catch (err) {
    console.warn(`[radio] localStorage read failed for ${key}`, err);
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch (err) {
    console.warn(`[radio] localStorage write failed for ${key}`, err);
  }
}

function describeMediaError(err: unknown): string {
  const name = err instanceof Error ? err.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone permission denied. Allow microphone access for this site in the browser's address bar, then press Start again.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No usable audio input found. Plug the radio cable into the laptop's line-in or a USB audio adapter and pick it from the list.";
    case "NotReadableError":
    case "AbortError":
      return "The audio input is busy or unreadable. Close other apps using the microphone and try again.";
    default:
      return err instanceof Error && err.message ? err.message : "Could not open the microphone.";
  }
}

function mediaDevicesAvailable(): boolean {
  return typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices !== "undefined" &&
    typeof navigator.mediaDevices.getUserMedia === "function";
}

function readInitialThreshold(): number {
  const stored = readStorage(RADIO_THRESHOLD_STORAGE_KEY);
  return stored === null ? RADIO_THRESHOLD_DEFAULT_DB : clampThresholdDb(Number(stored));
}

/**
 * Browser-side microphone capture for the Radio panel.
 *
 * Owns three coupled lifecycles: the mic (`getUserMedia` + Web Audio), the
 * `/radio-audio` WebSocket (reconnects with backoff while `enabled`), and the
 * energy VAD that gates which PCM blocks are shipped. Hosted above the drawer
 * so closing the panel does not stop listening.
 */
export function useRadioCapture({ serverUrl, onTranscript }: UseRadioCaptureOptions): RadioCaptureHandle {
  const supported = mediaDevicesAvailable();

  const [devices, setDevices] = useState<RadioInputDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [label, setLabelState] = useState(() => sanitizeRadioLabel(readStorage(RADIO_LABEL_STORAGE_KEY) ?? ""));
  const [thresholdDb, setThresholdState] = useState(readInitialThreshold);
  const [enabled, setEnabled] = useState(false);
  const [state, setState] = useState<RadioCaptureState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [levelDb, setLevelDb] = useState(-Infinity);
  const [vadOpen, setVadOpen] = useState(false);
  const [sentFrames, setSentFrames] = useState(0);

  const enabledRef = useRef(false);
  const labelRef = useRef(label);
  const onTranscriptRef = useRef(onTranscript);
  const wsUrlRef = useRef(deriveRadioWsUrl(serverUrl));
  const connectRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    labelRef.current = label;
    onTranscriptRef.current = onTranscript;
    wsUrlRef.current = deriveRadioWsUrl(serverUrl);
  }, [label, onTranscript, serverUrl]);

  const wsRef = useRef<WebSocket | null>(null);
  const readyRef = useRef(false);
  const seqRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffIndexRef = useRef(0);

  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const captureNodeRef = useRef<AudioNode | null>(null);
  const workletUrlRef = useRef<string | null>(null);
  const levelTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const vadRef = useRef(new EnergyVad({ thresholdDb }));
  const accumulatorRef = useRef(new BlockAccumulator(RADIO_BLOCK_SAMPLES));
  const latestLevelRef = useRef(-Infinity);

  const refreshDevices = useCallback(async () => {
    if (!supported || typeof navigator.mediaDevices.enumerateDevices !== "function") return;
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const inputs = all
        .filter((d) => d.kind === "audioinput")
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${i + 1}` }));
      setDevices(inputs);
      setSelectedDeviceId((current) =>
        current && !inputs.some((d) => d.deviceId === current) ? "" : current,
      );
    } catch (err) {
      console.warn("[radio] enumerateDevices failed", err);
    }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    const md = navigator.mediaDevices;
    const onChange = () => void refreshDevices();
    onChange();
    if (typeof md.addEventListener !== "function") return;
    md.addEventListener("devicechange", onChange);
    return () => md.removeEventListener("devicechange", onChange);
  }, [supported, refreshDevices]);

  const setLabel = useCallback((raw: string) => {
    const next = raw.slice(0, RADIO_LABEL_MAX_LENGTH);
    setLabelState(next);
    writeStorage(RADIO_LABEL_STORAGE_KEY, sanitizeRadioLabel(next));
  }, []);

  const setThresholdDb = useCallback((db: number) => {
    const next = clampThresholdDb(db);
    setThresholdState(next);
    vadRef.current.setThreshold(next);
    writeStorage(RADIO_THRESHOLD_STORAGE_KEY, String(next));
  }, []);

  const sendEnd = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && readyRef.current) {
      ws.send(JSON.stringify({ type: "end" }));
    }
  }, []);

  const handleBlock = useCallback((block: Float32Array) => {
    const step = vadRef.current.step(block);
    latestLevelRef.current = step.rmsDb;
    setVadOpen((prev) => (prev === step.open ? prev : step.open));

    if (step.open) {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN && readyRef.current) {
        ws.send(encodeRadioFrame(seqRef.current, block));
        seqRef.current = (seqRef.current + 1) >>> 0;
        setSentFrames((n) => n + 1);
      }
    } else if (step.justClosed) {
      sendEnd();
    }
  }, [sendEnd]);

  const handleChunk = useCallback((chunk: Float32Array, sourceRate: number) => {
    const pcm = sourceRate === RADIO_SAMPLE_RATE ? chunk : resampleLinear(chunk, sourceRate, RADIO_SAMPLE_RATE);
    for (const block of accumulatorRef.current.push(pcm)) handleBlock(block);
  }, [handleBlock]);

  const teardownAudio = useCallback(() => {
    if (levelTimerRef.current) {
      clearInterval(levelTimerRef.current);
      levelTimerRef.current = null;
    }
    captureNodeRef.current?.disconnect();
    captureNodeRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx && ctx.state !== "closed") {
      void ctx.close().catch((err: unknown) => console.warn("[radio] AudioContext close failed", err));
    }
    if (workletUrlRef.current) {
      URL.revokeObjectURL(workletUrlRef.current);
      workletUrlRef.current = null;
    }
    accumulatorRef.current.reset();
    vadRef.current.reset();
    latestLevelRef.current = -Infinity;
    setLevelDb(-Infinity);
    setVadOpen(false);
  }, []);

  const teardownSocket = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const ws = wsRef.current;
    wsRef.current = null;
    readyRef.current = false;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      ws.close();
    }
  }, []);

  const stop = useCallback(() => {
    enabledRef.current = false;
    setEnabled(false);
    if (vadRef.current.open) sendEnd();
    teardownSocket();
    teardownAudio();
    setState("idle");
  }, [sendEnd, teardownSocket, teardownAudio]);

  const connectSocket = useCallback(() => {
    if (!enabledRef.current) return;
    teardownSocket();

    const ws = new WebSocket(wsUrlRef.current);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    readyRef.current = false;
    seqRef.current = 0;

    ws.addEventListener("open", () => {
      if (wsRef.current !== ws) return;
      ws.send(JSON.stringify({
        type: "hello",
        sampleRate: RADIO_SAMPLE_RATE,
        label: sanitizeRadioLabel(labelRef.current),
      }));
    });

    ws.addEventListener("message", (event: MessageEvent) => {
      if (wsRef.current !== ws || typeof event.data !== "string") return;
      const msg = parseRadioServerMessage(event.data);
      if (!msg) return;
      switch (msg.type) {
        case "ready":
          readyRef.current = true;
          backoffIndexRef.current = 0;
          setError(null);
          setState("listening");
          break;
        case "transcript":
          onTranscriptRef.current(msg.transcript);
          break;
        case "error":
          setError(msg.message);
          break;
      }
    });

    const scheduleReconnect = () => {
      if (wsRef.current !== ws) return;
      wsRef.current = null;
      readyRef.current = false;
      if (!enabledRef.current) return;
      setState("reconnecting");
      const idx = Math.min(backoffIndexRef.current, BACKOFF_SCHEDULE_MS.length - 1);
      const base = BACKOFF_SCHEDULE_MS[idx];
      backoffIndexRef.current += 1;
      reconnectTimerRef.current = setTimeout(() => connectRef.current(), base + Math.random() * base * 0.25);
    };

    ws.addEventListener("close", scheduleReconnect);
    ws.addEventListener("error", () => {
      if (wsRef.current !== ws) return;
      setError("Server connection failed. Check that the VTMS server is reachable.");
      ws.close();
    });
  }, [teardownSocket]);

  useEffect(() => {
    connectRef.current = connectSocket;
  }, [connectSocket]);

  const startAudio = useCallback(async (deviceId: string) => {
    const constraints: MediaStreamConstraints = {
      audio: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        channelCount: 1,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = stream;

    let ctx: AudioContext;
    try {
      ctx = new AudioContext({ sampleRate: RADIO_SAMPLE_RATE });
    } catch (err) {
      console.warn("[radio] 16 kHz AudioContext refused, resampling in JS", err);
      ctx = new AudioContext();
    }
    audioCtxRef.current = ctx;
    const sourceRate = ctx.sampleRate;
    const source = ctx.createMediaStreamSource(stream);

    let captureNode: AudioNode | null = null;
    if (ctx.audioWorklet && typeof AudioWorkletNode === "function") {
      try {
        const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: "application/javascript" }));
        workletUrlRef.current = url;
        await ctx.audioWorklet.addModule(url);
        const node = new AudioWorkletNode(ctx, WORKLET_NAME, { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1 });
        node.port.onmessage = (event: MessageEvent) => {
          if (event.data instanceof Float32Array) handleChunk(event.data, sourceRate);
        };
        captureNode = node;
      } catch (err) {
        console.warn("[radio] AudioWorklet unavailable, falling back to ScriptProcessor", err);
      }
    }
    if (!captureNode) {
      const node = ctx.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER, 1, 1);
      node.onaudioprocess = (event: AudioProcessingEvent) => {
        handleChunk(event.inputBuffer.getChannelData(0).slice(0), sourceRate);
      };
      captureNode = node;
    }

    source.connect(captureNode);
    captureNode.connect(ctx.destination);
    captureNodeRef.current = captureNode;
    if (ctx.state === "suspended") await ctx.resume();

    for (const track of stream.getAudioTracks()) {
      track.addEventListener("ended", () => {
        if (!enabledRef.current) return;
        setError("The audio input disconnected. Reconnect the radio cable and press Start again.");
        stop();
        setState("error");
      });
    }

    levelTimerRef.current = setInterval(() => setLevelDb(latestLevelRef.current), LEVEL_PUBLISH_MS);
  }, [handleChunk, stop]);

  const start = useCallback(async () => {
    if (enabledRef.current) return;
    if (!supported) {
      setError("Microphone capture needs a secure context. Open the dashboard over https:// or on localhost.");
      setState("error");
      return;
    }
    enabledRef.current = true;
    setEnabled(true);
    setError(null);
    setSentFrames(0);
    backoffIndexRef.current = 0;
    setState("connecting");

    try {
      await startAudio(selectedDeviceId);
    } catch (err) {
      enabledRef.current = false;
      setEnabled(false);
      teardownAudio();
      setError(describeMediaError(err));
      setState("error");
      return;
    }
    if (!enabledRef.current) return;
    void refreshDevices();
    connectSocket();
  }, [supported, selectedDeviceId, startAudio, teardownAudio, refreshDevices, connectSocket]);

  const selectDevice = useCallback((deviceId: string) => {
    setSelectedDeviceId(deviceId);
  }, []);

  useEffect(() => () => {
    enabledRef.current = false;
    teardownSocket();
    teardownAudio();
  }, [teardownSocket, teardownAudio]);

  return {
    supported,
    devices,
    selectedDeviceId,
    selectDevice,
    refreshDevices,
    label,
    setLabel,
    thresholdDb,
    setThresholdDb,
    enabled,
    start,
    stop,
    state,
    error,
    levelDb,
    vadOpen,
    sentFrames,
  };
}
